import {
  Alert,
  Button,
  MoneyInput,
  RowActions,
  MultiSelect,
  NumberStepper,
  Text,
  useToast,
} from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage } from '../../../lib/errors';
import {
  lastAssortment,
  useMarketDays,
  usePrices,
  useSetAssortment,
  useSetMarketAssortment,
  type Market,
  type MarketDay,
} from '../api';
import { formatEuro } from '../format';
import styles from './sales.module.css';

interface Row {
  id: string;
  name: string;
  /** Market price in cents; `null` means the list price. */
  price: number | null;
  /** Taken along. */
  quantity: number | null;
  list: number | null;
  /** Jars in stock minus reservations of orders. */
  stock: number;
}

/** Jars that can still be sold: in stock minus reserved by orders. */
const available = (a: { jars: number; reserved: number }) => Math.max(0, a.jars - a.reserved);

/**
 * Assortment of a market day (MKT-008): the articles taken along, each with
 * an optional market price that replaces the list price for this day only.
 * Shows the stock per article (#135); on phones every article is a compact
 * list row, on wide screens a table.
 */
export function AssortmentDialog({
  day,
  market,
  onClose,
}: {
  /** Assortment of one market day … */
  day?: MarketDay;
  /** … or the standard assortment new days of a market start with. */
  market?: Market;
  onClose: () => void;
}) {
  const source = day ?? market;
  if (!source) throw new Error('AssortmentDialog needs a day or a market');
  const { t } = useTranslation();
  const toast = useToast();
  const prices = usePrices();
  const days = useMarketDays();
  const saveDay = useSetAssortment();
  const saveMarket = useSetMarketAssortment();
  const save = market ? saveMarket : saveDay;
  const catalog = (prices.data ?? []).filter((a) => !a.archived && a.current_cents != null);
  const [rows, setRows] = useState<Row[]>(
    source.assortment.map((a) => ({
      id: a.article.id,
      name: a.article.name,
      price: a.price_cents ?? null,
      quantity: a.quantity ?? null,
      list: a.list_price_cents ?? null,
      stock: 0,
    })),
  );
  const [hint, setHint] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);

  const patch = (id: string, change: Partial<Row>) =>
    setRows((r) => r.map((x) => (x.id === id ? { ...x, ...change } : x)));
  const fromCatalog = (id: string): Row | null => {
    const a = catalog.find((c) => c.article.id === id);
    return a
      ? {
          id,
          name: a.article.name,
          price: null,
          quantity: null,
          list: a.current_cents ?? null,
          stock: available(a),
        }
      : null;
  };
  const addAll = () => {
    setHint(null);
    setRows((current) => [
      ...current,
      ...catalog
        .filter((a) => available(a) > 0 && !current.some((r) => r.id === a.article.id))
        .map((a) => fromCatalog(a.article.id))
        .filter((r): r is Row => r !== null),
    ]);
  };
  const copyLast = async () => {
    setCopying(true);
    setHint(null);
    try {
      if (!day) return;
      const items = await lastAssortment(day, days.data ?? []);
      if (items.length === 0) {
        setHint(t('sales.assortment.noLast'));
        return;
      }
      setRows(
        items.map((a) => ({
          id: a.article.id,
          name: a.article.name,
          price: a.price_cents ?? null,
          quantity: a.quantity ?? null,
          list: catalog.find((c) => c.article.id === a.article.id)?.current_cents ?? null,
          stock: 0,
        })),
      );
    } finally {
      setCopying(false);
    }
  };
  // The catalog is loaded after the first render: fill in the stock of existing rows.
  const stockOf = (r: Row) => {
    const a = catalog.find((c) => c.article.id === r.id);
    return a ? available(a) : r.stock;
  };
  const move = (id: string, by: -1 | 1) =>
    setRows((all) => {
      const i = all.findIndex((x) => x.id === id);
      const j = i + by;
      const a = all[i];
      const b = all[j];
      if (!a || !b) return all;
      const next = [...all];
      next[i] = b;
      next[j] = a;
      return next;
    });
  const choose = (ids: string[]) =>
    setRows((current) => {
      const kept = current.filter((r) => ids.includes(r.id));
      const added = ids
        .filter((id) => !current.some((r) => r.id === id))
        .map(fromCatalog)
        .filter((r): r is Row => r !== null)
        .sort((a, b) => Number(b.stock > 0) - Number(a.stock > 0));
      return [...kept, ...added];
    });
  const ranked = [...catalog].sort((a, b) => Number(available(b) > 0) - Number(available(a) > 0));

  return (
    <FormDialog
      open
      drawer
      size="lg"
      onClose={onClose}
      title={t('sales.assortment.dialogTitle')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={save.error ? errorMessage(save.error) : undefined}
      onSubmit={() => {
        const items = rows.map((r) => ({
          article_id: r.id,
          price_cents: r.price,
          quantity: r.quantity,
        }));
        const done = {
          onSuccess: () => {
            toast.show(t('sales.assortment.saved'));
            onClose();
          },
        };
        if (market) saveMarket.mutate({ market: market.id, items }, done);
        else if (day) saveDay.mutate({ day: day.id, items }, done);
      }}
    >
      {day && day.summary.sales > 0 && <Alert tone="info">{t('sales.assortment.priceHint')}</Alert>}
      <div className={styles.quickActions}>
        <Button variant="secondary" size="sm" onClick={addAll}>
          {t('sales.assortment.addAll')}
        </Button>
        {day && (
          <Button variant="secondary" size="sm" loading={copying} onClick={() => void copyLast()}>
            {t('sales.assortment.copyLast')}
          </Button>
        )}
      </div>
      {hint && <Text tone="muted">{hint}</Text>}
      <MultiSelect
        label={t('sales.assortment.add')}
        title={t('sales.assortment.pickerTitle')}
        summary={t('sales.assortment.pickerSummary', { count: rows.length })}
        options={ranked.map((a) => ({
          id: a.article.id,
          label: `${a.article.name} · ${
            available(a) > 0
              ? t('sales.prices.inStock', { count: available(a) })
              : t('sales.assortment.noStock')
          }`,
        }))}
        selected={rows.map((r) => r.id)}
        onChange={choose}
        empty={t('sales.assortment.pickerEmpty')}
        labels={{
          search: t('sales.assortment.pickerSearch'),
          clearSearch: t('common.clear'),
          noMatch: t('common.noResults'),
          countOf: (count, total) => t('apiary.picker.countOf', { count, total }),
          selected: (count) => t('common.selected', { count }),
          selectAll: t('apiary.picker.selectAll'),
          selectNone: t('apiary.picker.selectNone'),
          done: t('apiary.picker.done'),
          cancel: t('common.cancel'),
          close: t('common.close'),
        }}
      />
      {rows.length === 0 ? (
        <Text tone="muted">{t('sales.assortment.emptyHint')}</Text>
      ) : (
        <ul className={styles.assortmentList} aria-label={t('sales.assortment.title')}>
          <li className={styles.assortmentHead} aria-hidden="true">
            <span>{t('sales.assortment.article')}</span>
            <span>{t('sales.assortment.stock')}</span>
            <span>{t('sales.market.taken')}</span>
            <span>{t('sales.assortment.price')}</span>
            <span />
          </li>
          {rows.map((r, i) => {
            const stock = stockOf(r);
            const out = stock === 0 && catalog.length > 0;
            const over = r.quantity != null && r.quantity > stock && !out;
            return (
              <li key={r.id} className={styles.assortmentItem} data-out={out || undefined}>
                <div className={styles.assortmentName}>
                  <span className={styles.assortmentTitle}>{r.name}</span>
                  <span className={styles.assortmentStock}>
                    {out
                      ? t('sales.assortment.noStock')
                      : t('sales.prices.inStock', { count: stock })}
                  </span>
                </div>
                <label className={`${styles.assortmentField} ${styles.assortmentTaken}`}>
                  <span className={styles.assortmentLabel}>{t('sales.market.taken')}</span>
                  <NumberStepper
                    value={r.quantity}
                    min={1}
                    aria-label={`${r.name}: ${t('sales.market.taken')}`}
                    onChange={(v) => patch(r.id, { quantity: v })}
                    labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                  />
                </label>
                <label className={`${styles.assortmentField} ${styles.assortmentPrice}`}>
                  <span className={styles.assortmentLabel}>{t('sales.assortment.price')}</span>
                  <MoneyInput
                    value={r.price}
                    aria-label={`${r.name}: ${t('sales.assortment.price')}`}
                    onChange={(v) => patch(r.id, { price: v })}
                    placeholder={r.list != null ? formatEuro(r.list) : ''}
                    labels={{ decrement: t('common.decrease'), increment: t('common.increase') }}
                  />
                </label>
                <div className={styles.assortmentMenu}>
                  <RowActions
                    label={t('sales.assortment.rowActions', { name: r.name })}
                    items={[
                      {
                        key: 'up',
                        label: t('sales.assortment.moveUp'),
                        disabled: i === 0,
                        onSelect: () => move(r.id, -1),
                      },
                      {
                        key: 'down',
                        label: t('sales.assortment.moveDown'),
                        disabled: i === rows.length - 1,
                        onSelect: () => move(r.id, 1),
                      },
                      {
                        key: 'remove',
                        label: t('sales.assortment.remove', { name: r.name }),
                        tone: 'danger',
                        separated: true,
                        onSelect: () => setRows((all) => all.filter((x) => x.id !== r.id)),
                      },
                    ]}
                  />
                </div>
                {over && (
                  <Text tone="warning" className={styles.assortmentWarn}>
                    {t('sales.assortment.onlyInStock', { count: stock })}
                  </Text>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </FormDialog>
  );
}
